import React, {
  forwardRef,
  DetailedHTMLProps,
  InputHTMLAttributes,
} from "react";

import classnames from "classnames";

import { useWombatTheme } from "../../theme";
import { IconProps } from "../../../icons";
import { TailSpin } from "../Loader/TailSpin";

export type ButtonVariant =
  | "primary"
  | "subtle"
  | "ghost"
  | "link"
  | "subtle-link"
  | "danger";
export type ButtonScale = "xs" | "sm" | "base" | "md" | "lg" | "inline";

export type ButtonProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  /**
   * Button type
   * @default 'button'
   */
  type?: "button" | "submit" | "reset";
  /**
   * Variant of the button
   * @default 'contained'
   */
  variant?: ButtonVariant;
  /**
   * Is the button round
   * @default false
   */
  rounded?: boolean;
  /**
   * Scale to be applied to the button
   * @default 'base'
   */
  scale?: ButtonScale;
  /**
   * Defines if the button is loading
   * By default a loading button will be disabled, to override this behaviour pass a 'disabled' prop
   * @default false
   */
  loading?: boolean;
  /**
   * Icon only button
   * @default undefined
   */
  icon?: React.ElementType<IconProps>;
  /**
   * Leading icon
   * @default undefined
   */
  leadingIcon?: React.ElementType<IconProps>;
  /**
   * Trailing icon
   * @default undefined
   */
  trailingIcon?: React.ElementType<IconProps>;
  /**
   * To ensure proper shadow a button element is wrapped within a span
   * Note: you probably don't want to alter those styles
   * @default: ''
   */
  containerClassname?: string;
  /**
   * If set to true, the button will take 100% width of it's parent container
   * @default false
   */
  block?: boolean;
  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
  /**
   * Meant to be used only by the Buttongroup component
   * @default false
   */
  __group__?: boolean;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      scale = "base",
      variant = "primary",
      block = false,
      rounded = false,
      type = "button",
      icon = null,
      leadingIcon = null,
      trailingIcon = null,
      loading = false,
      className,
      containerClassname = "",
      disabled,
      __group__ = false,
      children,
      ...props
    },
    ref
  ) => {
    const { theme } = useWombatTheme();

    const containerStyle = classnames(
      theme.button.container[__group__ ? "__group__" : variant],
      block && theme.button.block,
      rounded && theme.button.rounded,
      containerClassname
    );
    const buttonStyle = classnames(
      __group__ ? theme.button.grouped : theme.button.base,
      theme.button.variant[variant],
      theme.button.scale[scale],
      rounded && theme.button.rounded,
      block && theme.button.block,
      className && className
    );

    const Icon = icon;
    const LeadingIcon = leadingIcon;
    const TrailingIcon = trailingIcon;

    const iconStyle = classnames(theme.button.icon[scale].only);
    const leadingIconStyle = classnames(theme.button.icon[scale].leading);
    const trailingIconStyle = classnames(theme.button.icon[scale].trailing);

    const loaderContainerStyle = classnames(
      theme.button.loader[variant]
    );
    const loaderStyle = classnames(theme.button.icon[scale].only);

    return (
      <span className={containerStyle}>
        <button
          ref={ref}
          disabled={loading ? true : disabled}
          aria-disabled={loading || disabled ? "true" : "false"}
          type={type}
          className={buttonStyle}
          {...props}
        >
          {loading && (
            <span className={loaderContainerStyle}>
              <TailSpin className={loaderStyle} />
            </span>
          )}
          {icon && <Icon className={iconStyle} />}
          {!icon && leadingIcon && (
            <LeadingIcon className={leadingIconStyle} />
          )}
          {!icon && children}
          {!icon && trailingIcon && (
            <TrailingIcon className={trailingIconStyle} />
          )}
        </button>
      </span>
    );
  }
);

Button.displayName = "Button";

export default Button;
